import React, { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { styled } from '@mui/system';

import { CustomDialog } from '../../../components/StyledComponents';
import {
  ControlpanelPrimaryButton,
  ControlpanelSecondaryButton,
  ControlpanelSecondaryButtonV2,
  GrayIconButton,
} from '../../../components/StyledButton';
import CustomInput from '../../../components/CustomInput';

import RefreshCircleIcon20pxSvgV2 from '../../../assets/images/icons/RefreshCircleIcon20pxSvgV2';
import ConfirmResetEntitlement from '../LeaveAndHoliday/ConfirmResetEntitlement';
import { editEntitlement } from '../../../axios/User/UserApi';
import { toast } from 'react-toastify';

/* ---------- styled ---------- */
const CustomSecondaryButton = styled(ControlpanelSecondaryButtonV2)(() => ({
  minWidth: '40px',
  '.MuiSvgIcon-root': {
    fontSize: '20px',
  },
}));

/* ---------- component ---------- */
const EditEntitlementForm = (props) => {
  const { t, i18n } = useTranslation();

  const {
    open,
    handleClose,
    leaveByUser, // list entitlement hiện tại
    userId,
    year,
    onSuccess,
  } = props;

  /** state */
  const [entitlements, setEntitlements] = useState([]);
  const [openConfirmReset, setOpenConfirmReset] = useState(false);
  const [selectedEntitlement, setSelectedEntitlement] = useState(null);

  /** init data khi mở dialog */
  useEffect(() => {
    if (open && Array.isArray(leaveByUser)) {
      setEntitlements(
        leaveByUser.map((item) => ({
          ...item,
          totalEntitlement: item.totalEntitlement ?? item.entitlement, // 👈 value hiển thị
          originalEntitlement: item.entitlement, // 👈 value gốc để reset
        }))
      );
    }
  }, [open, leaveByUser]);

  /* ---------- helpers ---------- */
  const translateLeaveTypeName = (leaveTypeName) => {
    if (!leaveTypeName) return '-';

    const map = {
      Annual: t('common.anual'),
      Casual: t('common.casual'),
      Medical: t('common.medical'),
      'Arrive late and leave early': t('common.arrivelateandearly'),
    };

    return map[leaveTypeName] || leaveTypeName;
  };

  const getLeaveTypeRemark = (item) => {
    if (!item) return '-';
    const isEnglish = i18n.language === 'en';
    return isEnglish
      ? item.remarkEN || item.remark
      : item.remark || item.remarkEN;
  };

  // 9.00 -> 9; 9.5 -> 9.5
  const formatEntitlementNumber = (value) => {
    const numberValue = Number(value);
    if (!Number.isFinite(numberValue)) return value ?? 0;
    if (Number.isInteger(numberValue)) return numberValue;
    return Number(numberValue.toFixed(2));
  };

  /* ---------- reset ---------- */
  const handleOpenConfirmReset = (item) => {
    setSelectedEntitlement(item);
    setOpenConfirmReset(true);
  };

  const handleConfirmReset = () => {
    if (!selectedEntitlement) return;

    setEntitlements((prev) =>
      prev.map((e) =>
        e.leaveTypeId === selectedEntitlement.leaveTypeId
          ? { ...e, totalEntitlement: e.originalEntitlement }
          : e
      )
    );

    const name =
      i18n.language === "vi"
        ? selectedEntitlement.remark
        : selectedEntitlement.remarkEN;

    toast.success(
      <Trans
        i18nKey="common.resetSuccess"
        values={{ name }}
        components={{ strong: <strong /> }}
      />
    );

    setOpenConfirmReset(false);
    setSelectedEntitlement(null);
  };

  const handleSubmit = async () => {
    try {
      const changedEntitlements = entitlements

      const responses = await Promise.all(
        changedEntitlements.map((item) =>
          editEntitlement({
            userId,
            leaveTypeId: item.leaveTypeId,
            year,
            entitlement: Number(item.totalEntitlement),
            langId: localStorage.getItem('i18nextLngId'),
          })
        )
      );

      // kiểm tra tất cả response
      const allSuccess = responses.every(
        (res) => res?.data?.responseCode === 0
      );

      if (allSuccess) {
        toast.success(
          t('toast.update-entitlement-success') ||
          'Update entitlement successfully'
        );
        props.onReload?.();
        handleClose();
        onSuccess?.();
      } else {
        const firstError = responses.find(
          (res) => res?.data?.code !== 200
        );

        toast.error(
          firstError?.data?.message ||
          t('toast.update-entitlement-failed') ||
          'Update entitlement failed'
        );
      }
    } catch (error) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t('toast.update-entitlement-failed') ||
        'Update entitlement failed';

      toast.error(message);
    }
  };

  /* ---------- render ---------- */
  return (
    <>
      <CustomDialog maxWidth="sm" open={open} onClose={handleClose} keepMounted>
        <DialogTitle>
          <div className="flex justify-between">
            <div className="flex items-center gap-4">
              <div className="w-4 h-8 rounded bg-[#D9F99D]" />
              <p className="text-xl font-semibold">
                {t('button.edit-entitlements')}
              </p>
            </div>
            <GrayIconButton onClick={handleClose}>
              <CloseIcon />
            </GrayIconButton>
          </div>
        </DialogTitle>

        <DialogContent>
          <div className="space-y-4 mt-2">
            {entitlements
              .filter((item) => item.leaveTypeId !== 1)
              .map((item) => (
                <div key={item.leaveTypeId} className="space-y-1">
                  <p className="font-medium">
                    {translateLeaveTypeName(getLeaveTypeRemark(item))}{' '}
                    <span className="text-gray-500 italic">
                      ({t('common.taken')} {formatEntitlementNumber(item.usedFromYear)}{' '}
                      {t('common.outOf')} {formatEntitlementNumber(item.totalEntitlement)})
                    </span>
                  </p>

                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <CustomInput
                        inputType="number"
                        value={item.totalEntitlement ?? 0}
                        labelEnable={false}
                        className="w-full"
                        inputProps={{ min: 0 }}
                        handleInputChange={(e) => {
                          let value = e.target.value;

                          // ❗ Nếu xóa hết → về 0
                          if (value === '' || value === null) {
                            value = 0;
                          }

                          // ❗ Ép về số
                          value = Number(value);

                          // ❗ Không cho âm
                          if (value < 0 || isNaN(value)) {
                            value = 0;
                          }

                          setEntitlements((prev) =>
                            prev.map((ent) =>
                              ent.leaveTypeId === item.leaveTypeId
                                ? { ...ent, totalEntitlement: value }
                                : ent
                            )
                          );
                        }}
                      />
                    </div>

                    <CustomSecondaryButton
                      onClick={() => handleOpenConfirmReset(item)}
                    >
                      <RefreshCircleIcon20pxSvgV2 />
                    </CustomSecondaryButton>
                  </div>
                </div>
              ))}
          </div>
        </DialogContent>

        <DialogActions>
          <div className="flex gap-2 px-4 py-4">
            <ControlpanelSecondaryButton onClick={handleClose}>
              {t('button.cancel')}
            </ControlpanelSecondaryButton>
            <ControlpanelPrimaryButton onClick={handleSubmit}>
              {t('button.confirm')}
            </ControlpanelPrimaryButton>
          </div>
        </DialogActions>
      </CustomDialog>

      {/* confirm reset */}
      <ConfirmResetEntitlement
        open={openConfirmReset}
        onClose={() => setOpenConfirmReset(false)}
        onConfirm={handleConfirmReset}
        leaveType={
          selectedEntitlement
            ? translateLeaveTypeName(getLeaveTypeRemark(selectedEntitlement))
            : ''
        }
      />
    </>
  );
};

export default EditEntitlementForm;
